import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Link,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import logo from '../../assets/images/2019_QuickClaimLogo_Circle.png';
import paths from '../../router/paths';
import {
  deleteNotification,
  getNotification,
  getNotificationStats,
  updateNotification,
} from '../../services/api-helpers/utility';
import Auth from '../../services/auth';
import { UPDATE_NOTIFICATION_STATS, UPDATE_NOTIFICATIONS } from '../../store/actions/action-types';
import { useDispatchToProps, useStateToProps } from '../../store/actions/actions';
import { orgIdSelector } from '../../store/selectors';
import ConfirmDialog from '../dialog-confirm';

function NotificationItem({ createDate, isRead, message, notificationId, type, url }) {
  const { orgId, userNotifications } = useStateToProps((state) => ({
    orgId: orgIdSelector(state),
    userNotifications: state.app.notifications,
  }));
  const updateNotificationStats = useDispatchToProps(UPDATE_NOTIFICATION_STATS);
  const updateNotificationStore = useDispatchToProps(UPDATE_NOTIFICATIONS);
  const [checked, setChecked] = useState(isRead);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleGetNotificationStats = async () => {
    const userId = Auth().getUserId();
    try {
      const { body, ok } = await getNotificationStats({
        userId,
        orgId,
        timezone: moment.tz.guess(),
      });
      if (ok) {
        updateNotificationStats(body.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleUpdateNotifications = async (id, isReadNotification) => {
    const userId = Auth().getUserId();
    try {
      const { ok } = await updateNotification({
        userId,
        orgId,
        notificationId: id,
        isRead: isReadNotification,
      });
      if (ok) {
        const data = [...userNotifications.list];
        data.map((item) => {
          if (item.notificationId === id) {
            item.isRead = Number(isReadNotification);
          }
          return item;
        });
        const unreadList = data.filter((item) => !item.isRead);
        updateNotificationStore({
          list: data,
          unreadList,
          loading: false,
        });
        await handleGetNotificationStats();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleDeleteNotifications = async () => {
    const userId = Auth().getUserId();
    try {
      const { ok } = await deleteNotification({
        userId,
        orgId,
        notificationId,
      });
      if (ok) {
        const updatedList = userNotifications.list.filter(
          (itemInArray) => itemInArray.notificationId !== notificationId,
        );

        const unreadList = updatedList.filter((item) => !item.isRead);
        updateNotificationStore({
          list: updatedList,
          unreadList,
          loading: false,
        });
        await handleGetNotificationStats();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleChange = async (event) => {
    setChecked(event.target.checked);
    await handleUpdateNotifications(notificationId, event.target.checked);
  };

  return (
    <>
      <Box
        p={2}
        sx={{
          backgroundColor: (theme) =>
            isRead ? theme.palette.background.light : theme.palette.background.lightBlue,
          borderRadius: '5px',
          marginBottom: '5px',
          position: 'relative',
          '& .notification-actions': {
            position: 'absolute',
            bottom: '5px',
            right: '5px',
            opacity: 0,
            padding: '1px 5px',
            borderRadius: '3px',
            transition: (theme) =>
              theme.transitions.create('opacity', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          },
          '&:hover .notification-actions': {
            opacity: 1,
          },
          '& .MuiCheckbox-root, & .MuiIconButton-root': {
            padding: '6px',
          },
        }}
      >
        <Box display="flex" alignItems="flex-start">
          <Box
            sx={{
              width: '50px',
              height: '50px',
              marginRight: '15px',
              fontSize: '50px',
              '& img': {
                width: '50px',
                height: '50px',
              },
            }}
          >
            {type === 'Error' ? (
              <Typography color="secondary" sx={{ fontSize: '50px' }}>
                <HighlightOffIcon fontSize="inherit" color="inherit" />
              </Typography>
            ) : type === 'Update' ? (
              <Typography color="primary" sx={{ fontSize: '50px' }}>
                <InfoOutlinedIcon fontSize="inherit" color="inherit" />
              </Typography>
            ) : type === 'Warning' ? (
              <Typography color="primary" sx={{ fontSize: '50px' }}>
                <WarningAmberOutlinedIcon fontSize="inherit" color="warning" />
              </Typography>
            ) : (
              <img alt="Logo" src={logo} />
            )}
          </Box>
          <Box>
            <Box display="flex" alignItems="center">
              <Box pr={1}>
                <Typography variant="subtitle2">quickclaim</Typography>
              </Box>
              <Typography color="textSecondary" variant="subtitle2">
                {`${moment(createDate).fromNow()}`}
              </Typography>
            </Box>
            <Box
              sx={{
                marginTop: '5px',
                overflowWrap: 'anywhere',
                '& a': {
                  marginLeft: '5px',
                },
              }}
            >
              <Typography variant="body1" display="inline">
                {message}
              </Typography>
              {url && (
                <Link href={url} variant="body2" target="_blank">
                  {url}
                </Link>
              )}
            </Box>
            <Box className="notification-actions" display="flex" alignItems="center">
              <Tooltip title={isRead ? 'Mark as unread' : 'Mark as read'} arrow>
                <Checkbox
                  inputProps={{
                    'aria-label': isRead ? 'Mark as unread' : 'Mark as read',
                  }}
                  icon={<CircleOutlinedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  checked={checked}
                  onChange={handleChange}
                  size="medium"
                />
              </Tooltip>
              <Tooltip title="Delete" arrow>
                <IconButton aria-label="delete" onClick={() => setDeleteConfirmOpen(true)}>
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
      <ConfirmDialog
        title="Delete Notification"
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        onConfirm={handleDeleteNotifications}
      >
        <Typography variant="body1">Are you sure you want to delete this notification?</Typography>
      </ConfirmDialog>
    </>
  );
}

NotificationItem.propTypes = {
  url: PropTypes.string,
  createDate: PropTypes.string.isRequired,
  isRead: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  notificationId: PropTypes.number.isRequired,
};

NotificationItem.defaultProps = {
  url: null,
};

function Notification() {
  const history = useHistory();

  const { userNotifications, orgId } = useStateToProps((state) => ({
    userNotifications: state.app.notifications,
    orgId: orgIdSelector(state),
  }));
  const updateNotificationStore = useDispatchToProps(UPDATE_NOTIFICATIONS);

  const [notificationPanel, setNotificationState] = useState(false);
  const [tabValue, setTabValue] = useState('UNREAD');

  const handleGetNotifications = async (loading = true) => {
    updateNotificationStore({
      // list: [],
      // unreadList: [],
      loading,
    });
    const userId = Auth().getUserId();
    try {
      const { body, ok } = await getNotification({ userId, orgId, timezone: moment.tz.guess() });
      if (ok) {
        const unreadList = body.data.filter((item) => !item.isRead);
        updateNotificationStore({
          list: body.data,
          unreadList,
          loading: false,
          // unReadRecords: unreadList.length,
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleUpdateNotifications = async (id, isReadNotification) => {
    const userId = Auth().getUserId();
    try {
      const { ok } = await updateNotification({
        userId,
        orgId,
        notificationId: id,
        isRead: isReadNotification,
      });
      if (ok) {
        await handleGetNotifications(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleMarkAll = async (isRead) => {
    await handleUpdateNotifications('ALL', isRead);
  };

  useEffect(() => {
    if (notificationPanel === true) {
      (async () => {
        await handleGetNotifications();
      })();
    }
  }, [notificationPanel]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setNotificationState(open);
  };

  const handleGoToNDISSettingPage = (event) => {
    toggleDrawer(false)(event);
    history.push(paths.ndisNotifications);
  };

  const renderLoading = () => (
    <>
      <Box pb={1}>
        <Skeleton variant="rectangular" height={100} />
      </Box>
      <Box pb={1}>
        <Skeleton variant="rectangular" height={100} />
      </Box>
      <Box pb={1}>
        <Skeleton variant="rectangular" height={100} />
      </Box>
    </>
  );

  return (
    <>
      <IconButton
        sx={{
          marginRight: '10px',
          marginLeft: '5px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          fontSize: '18px',
        }}
        aria-label="notification"
        onClick={toggleDrawer(true)}
      >
        <Badge badgeContent={userNotifications?.unReadRecords || 0} color="error" max={10000000}>
          <NotificationsOutlinedIcon color="action" />
        </Badge>
      </IconButton>
      <SwipeableDrawer
        anchor="right"
        open={notificationPanel}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '600px',
            maxWidth: '100%',
          },
          '& .MuiTab-root': {
            minWidth: '100px',
          },
        }}
      >
        <Box p={1} justifyContent="space-between" display="flex" alignItems="flex-start">
          <Box p={2} pb={0}>
            <Typography color="textSecondary" gutterBottom variant="h2">
              Notifications
            </Typography>
          </Box>
          <Box>
            <IconButton
              aria-label="ndis settings"
              onClick={(event) => handleGoToNDISSettingPage(event)}
            >
              <SettingsIcon />
            </IconButton>
            <IconButton aria-label="close" onClick={toggleDrawer(false)}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </Box>
        <Box px={3}>
          <AppBar position="sticky" color="inherit" style={{ boxShadow: 'none' }}>
            <Box
              sx={{
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                position: 'relative',
              }}
            >
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="Notification Tab">
                <Tab label="Unread" value="UNREAD" />
                <Tab label="All" value="ALL" />
              </Tabs>
              <Box
                sx={{
                  position: 'absolute',
                  right: '10px',
                  top: '1px',
                }}
              >
                <Tooltip title="Mark all as read" arrow>
                  <IconButton aria-label="mark all read" onClick={() => handleMarkAll(true)}>
                    <CheckCircleIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Mark all as unread" arrow>
                  <IconButton aria-label="mark all unread" onClick={() => handleMarkAll(false)}>
                    <CircleOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </AppBar>

          <Box sx={{ mt: 2, display: tabValue === 'UNREAD' ? 'block' : 'none' }}>
            {userNotifications.loading ? (
              renderLoading()
            ) : userNotifications?.unreadList?.length === 0 ? (
              <Typography color="textSecondary" align="center">
                There aren&#39;t any notifications to display.
              </Typography>
            ) : (
              userNotifications?.unreadList?.map((item, index) => (
                <NotificationItem
                  key={index}
                  notificationId={item.notificationId}
                  createDate={item.createDate}
                  isRead={Boolean(item.isRead)}
                  message={item.message}
                  url={item.url}
                  type={item.type}
                />
              ))
            )}
          </Box>

          <Box sx={{ mt: 2, display: tabValue === 'ALL' ? 'block' : 'none' }}>
            {userNotifications.loading ? (
              renderLoading()
            ) : userNotifications?.list?.length === 0 ? (
              <Typography color="textSecondary" align="center">
                There aren&#39;t any notifications to display.
              </Typography>
            ) : (
              userNotifications?.list?.map((item, index) => (
                <NotificationItem
                  key={index}
                  notificationId={item.notificationId}
                  createDate={item.createDate}
                  isRead={Boolean(item.isRead)}
                  message={item.message}
                  url={item.url}
                  type={item.type}
                />
              ))
            )}
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
}

export default Notification;
